import { useState, useEffect, useCallback, useMemo } from "react";
import { IAircraft } from "@vmosolutions/oc-interfaces";
import { FormikSelectField } from "src/components/FormikComponents/FormikWrappers";
import { ISelectFieldProps } from "src/components/FormikComponents/SelectField";
// API Calls
import AmplifyAPIWrapper from "src/API/AmplifyAPIWrapper";
import { formatFunction, parseFunction } from "./helpers";
import _ from "lodash";

interface IAircraftSelectFieldProps extends Omit<ISelectFieldProps, "options"> {
  mosaicPane: HTMLElement;
  valueKey?: string; // valueKey sent back as value - use entire object otherwise.
  showInactive?: boolean;
  groupByField?: string;
  omitSub?: boolean;
  omitPlanning?: boolean;
}

// Takes in a list of Aircraft and returns them grouped by field provided.
const formatAircraftList = (aircraft: IAircraft[], groupByField?: string): any => {
  if (aircraft.length > 0) {
    const aircraftGroups = aircraft.reduce((obj: { [key: string]: any }, AC) => {
      // Sort aircraft
      const title = _.get(AC, groupByField);
      if (!obj[title]) {
        obj[title] = [AC];
      } else {
        obj[title].push(AC);
      }
      return obj;
    }, {});
    return Object.keys(aircraftGroups).map((title: string) => ({
      label: title,
      options:
        aircraftGroups?.[title]?.sort((a: IAircraft, b: IAircraft) => {
          return a.registration.toLowerCase().localeCompare(b.registration.toLowerCase());
        }) ?? [],
    }));
  } else {
    return [];
  }
};

/**
 * Sepcialized wrapper for "built-in" oc aircraft select field.
 * @param props -
 */
export const AircraftSelectField = (props: IAircraftSelectFieldProps): JSX.Element => {
  const {
    mosaicPane,
    label,
    name,
    onChange,
    valueKey,
    isMulti,
    defaultValue,
    groupByField,
    omitSub = false,
    omitPlanning = false,
    ...selectProps
  } = props;

  /////////////////////////
  // MANAGE AIRCRAFT LIST
  /////////////////////////
  const [aircraft, setAircraft] = useState<IAircraft[]>([]);

  useEffect(() => {
    const getAircraftAction = async (): Promise<void> => {
      //const notifications: INotifications = { dispatch, errorMessage: "No Aircraft found." };
      const response = await AmplifyAPIWrapper.Get<IAircraft[]>("/aircraft");
      if (Array.isArray(response)) {
        const filteredResponse = response.filter((ac) => {
          if ((omitSub && ac.subCharter) || (omitPlanning && ac.planning)) {
            return false;
          }
          return true;
        });
        setAircraft(filteredResponse);
      }
    };
    getAircraftAction();
  }, [omitSub, omitPlanning]);

  // Setup Format And Parse functions (used if valueKey is passed)
  const formatValue = useCallback(
    (value: string) => formatFunction(value, aircraft, valueKey, isMulti),
    [aircraft, valueKey, isMulti]
  );
  const parseOption = useCallback((option: string) => parseFunction(option, valueKey, isMulti), [valueKey, isMulti]);
  const aircraftOptions = useMemo(() => {
    return groupByField ? formatAircraftList(aircraft, groupByField) : aircraft;
  }, [groupByField, aircraft]);

  return (
    <FormikSelectField
      name={name}
      label={label ?? "Aircraft Selector"}
      isMulti={isMulti}
      options={aircraftOptions}
      getOptionLabel={(ac: IAircraft) => `${ac.registration} - ${ac.aircraftType?.title}`}
      getOptionValue={(aircraft: IAircraft) => aircraft._id}
      format={(value) => (valueKey ? formatValue(value) : value)}
      parse={(option) => (valueKey ? parseOption(option) : option)}
      onChange={(value: any) => !!onChange && onChange(value)}
      menuPortalTarget={mosaicPane}
      menuPosition={mosaicPane ? "fixed" : undefined}
      isSearchable={true}
      openMenuOnFocus={false}
      isClearable={true}
      placeholder="Select Aircraft"
      {...selectProps} // override with any props passed in such as isMulti, format, parse.
    />
  );
};
